/**
 * Performance monitoring utilities
 * Tracks Core Web Vitals and custom metrics
 */

interface Metric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta?: number;
}

type MetricHandler = (metric: Metric) => void;

const handlers: MetricHandler[] = [];

export function onMetric(handler: MetricHandler) {
  handlers.push(handler);
  return () => {
    const index = handlers.indexOf(handler);
    if (index > -1) handlers.splice(index, 1);
  };
}

// React specific hook to avoid unmount leaks
export function usePerformanceMetric(handler: MetricHandler) {
  // Use 'React.useEffect' safely if React is in scope, otherwise standard 
  if (typeof window !== 'undefined' && (window as any).React) {
    (window as any).React.useEffect(() => {
      return onMetric(handler);
    }, [handler]);
  }
}

function reportMetric(metric: Metric) {
  if (import.meta.env.DEV) {
    console.log(`[Performance] ${metric.name}:`, metric.value, metric.rating);
  }
  handlers.forEach(handler => {
    try {
      handler(metric);
    } catch {
      // Ignore errors in handlers
    }
  });
}

function getRating(name: string, value: number): Metric['rating'] {
  switch (name) {
    case 'CLS':
      return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
    case 'FID':
    case 'INP':
      return value <= 100 ? 'good' : value <= 300 ? 'needs-improvement' : 'poor';
    case 'LCP':
      return value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
    case 'FCP':
      return value <= 1800 ? 'good' : value <= 3000 ? 'needs-improvement' : 'poor';
    case 'TTFB':
      return value <= 800 ? 'good' : value <= 1800 ? 'needs-improvement' : 'poor';
    default:
      return 'good';
  }
}

// Simple polyfill for performance.measure if needed
function measure(metricName: string, startMark?: string, endMark?: string): number | undefined {
  if (typeof performance === 'undefined') return undefined;
  
  try {
    if (startMark && endMark) {
      const measure = performance.measure(metricName, startMark, endMark);
      return measure.duration;
    }
    return performance.now();
  } catch {
    return undefined;
  }
}

// Track First Contentful Paint
export function trackFCP() {
  if (typeof performance === 'undefined') return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          const value = entry.startTime;
          reportMetric({
            name: 'FCP',
            value,
            rating: getRating('FCP', value),
          });
          observer.disconnect();
        }
      }
    });
    observer.observe({ entryTypes: ['paint'] });
  } catch {
    // PerformanceObserver not supported
  }
}

// Track Largest Contentful Paint
export function trackLCP() {
  if (typeof performance === 'undefined') return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        const value = lastEntry.startTime;
        reportMetric({
          name: 'LCP',
          value,
          rating: getRating('LCP', value),
        });
      }
    });
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
  } catch {
    // PerformanceObserver not supported
  }
}

// Track Time to First Byte
export function trackTTFB() {
  if (typeof performance === 'undefined') return;
  
  try {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      const value = navEntry.responseStart;
      reportMetric({
        name: 'TTFB',
        value,
        rating: getRating('TTFB', value),
      });
    }
  } catch {
    // Navigation timing not supported
  }
}

// Track First Input Delay
export function trackFID() {
  if (typeof performance === 'undefined') return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const fidEntry = entry as PerformanceEventTiming;
        const value = fidEntry.processingStart - fidEntry.startTime;
        reportMetric({
          name: 'FID',
          value,
          rating: getRating('FID', value),
        });
        observer.disconnect();
      }
    });
    observer.observe({ entryTypes: ['first-input'] });
  } catch {
    // PerformanceObserver not supported
  }
}

// Track custom render time
export function trackRenderTime(componentName: string) {
  const markName = `${componentName}-render`;
  
  return {
    start: () => {
      if (typeof performance !== 'undefined') {
        performance.mark(`${markName}-start`);
      }
    },
    end: () => {
      if (typeof performance !== 'undefined') {
        performance.mark(`${markName}-end`);
        const duration = measure(markName, `${markName}-start`, `${markName}-end`);
        if (duration !== undefined) {
          reportMetric({
            name: `render-${componentName}`,
            value: duration,
            rating: duration < 16 ? 'good' : duration < 50 ? 'needs-improvement' : 'poor',
          });
        }
      }
    },
  };
}

// Initialize all tracking
export function initPerformanceMonitoring() {
  if (typeof window === 'undefined') return;
  
  // Delay to ensure the page is fully loaded
  if (document.readyState === 'complete') {
    trackAllMetrics();
  } else {
    window.addEventListener('load', trackAllMetrics);
  }
}

function trackAllMetrics() {
  // Use requestIdleCallback to avoid blocking main thread
  const schedule = (typeof requestIdleCallback !== 'undefined') 
    ? requestIdleCallback 
    : (cb: () => void) => setTimeout(cb, 1);
  
  schedule(() => {
    trackTTFB();
    trackFCP();
    trackLCP();
    trackFID();
  });
}
